import type { CheckboxOption } from '../components/IntegrationRequestForm/CheckboxListField';
import type { IntegrationRequestFormOptions } from '../../../models/IntegrationRequest';
import type { ShippingStatus } from '@/pages/Business/pages/LogisticOperator/models';
import { useMemo } from 'react';

interface UseIntegrationRequestFormChoicesProps {
  options?: IntegrationRequestFormOptions;
  logisticOperatorId: string;
}

const useIntegrationRequestFormChoices = ({
  options,
  logisticOperatorId,
}: UseIntegrationRequestFormChoicesProps) => {
  const operator = options?.logisticOperators.find(
    (item) => item.id === logisticOperatorId
  );

  const operatorOptions = useMemo(
    () =>
      (options?.logisticOperators ?? []).map((item) => ({
        value: item.id,
        label: item.code ? `${item.name} (${item.code})` : item.name,
      })),
    [options]
  );

  const serviceOptions = useMemo<CheckboxOption[]>(
    () =>
      (operator?.services ?? []).map((service) => ({
        value: service.id,
        label: `${service.name} (${service.apiCode})`,
        hint: service.enabledForCommerce ? 'habilitado al comercio' : 'se habilita al integrar',
      })),
    [operator]
  );

  const branchOptions = useMemo<CheckboxOption[]>(
    () =>
      (options?.branches ?? []).map((branch) => ({
        value: branch.id,
        label: branch.name,
        hint: branch.externalBranchCode ? `código ${branch.externalBranchCode}` : 'sin código',
      })),
    [options]
  );

  const notifiableStatuses = useMemo<ShippingStatus[]>(
    () =>
      (options?.notifiableStatuses ?? []).map((status) => ({
        id: status.id,
        name: status.name,
        commercialName: status.commercialName ?? undefined,
      })),
    [options]
  );

  return {
    operatorOptions,
    serviceOptions,
    branchOptions,
    notifiableStatuses,
  };
};

export default useIntegrationRequestFormChoices;
